import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

// V2 pill button (etn-btn in button.css): 13px/600 control type, pill
// radius, 30/32/36px control heights. The Tinted Accent Rule — default is a
// green tint with deep-green ink, never a solid fill — falls out of
// --primary itself now resolving to the tint (see globals.css :root), so
// `bg-primary text-primary-foreground` is tinted by construction. destructive
// and outline are spelled out explicitly since their shadcn defaults
// hardcode a solid fill + white text that the --primary swap can't reach.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[7px] whitespace-nowrap rounded-etn-pill text-etn-control font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-etn-accent-tint-hover",
        destructive:
          "bg-etn-danger-subtle text-etn-danger-text hover:bg-etn-danger-subtle-hover focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-etn-border-input bg-etn-surface text-etn-text-cell hover:bg-etn-control-subtle dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-etn-control-subtle-hover",
        ghost:
          "hover:bg-etn-control-subtle hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-etn-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[var(--etn-control-height-md)] px-4 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-etn-pill px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-[var(--etn-control-height-sm)] rounded-etn-pill gap-1.5 px-3 text-etn-control-sm has-[>svg]:px-2.5",
        lg: "h-[var(--etn-control-height-input)] rounded-etn-pill px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-etn-pill [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
